/* eslint-disable prettier/prettier */
import { isWithinInterval, compareAsc, startOfDay, endOfDay, intlFormat } from "date-fns";
export const getDate = ({ value, throwOnError = true, format = getLocalDateFormat() }) => {
    if (!value) {
        return null;
    }
    if (value instanceof Date) {
        return value;
    }
    const isValidDate = !isNaN(new Date(value).getTime());
    if (!isValidDate) {
        if (throwOnError) {
            throw Error(`Invalid Date Entered`);
        }
        return null;
    }
    //Consider UTC specified in string
    if (value.includes("Z")) {
        return new Date(value);
    }
    //Split up the month, day and year
    const splitDate = value.split("/");
    let day, month, year;
    if (format === "MM/dd/yyyy") {
        [month, day, year] = splitDate;
    }
    else {
        [day, month, year] = splitDate;
    }
    return new Date(+year, +month - 1, +day); //Months have 0 start in date object
};
/**
 * Gets date in ms (number), saving complete timestamp.
 * @param date value to convert
 * @returns date in ms
 */
export const getDateInMs = ({ value }) => {
    if (!value) {
        return null;
    }
    if (value instanceof Date) {
        return value.getTime();
    }
    //string type, try converting to date type
    const isValidDate = !isNaN(new Date(value).getTime());
    if (!isValidDate) {
        return null;
    }
    return new Date(value).getTime();
};
/**
 * Utility function to check if entered date is greater than or equal to reference date  with ms accuracy
 * @param date date to compare with reference date
 * @param minDate reference date
 * @returns boolean value indicating the comparison result
 */
export const isGreaterThanOrEqual = (date, minDate) => {
    const dateToTest = getDateInMs({ value: date });
    const referenceDate = getDateInMs({ value: minDate });
    if (!dateToTest || !referenceDate) {
        return false;
    }
    const result = referenceDate - dateToTest;
    if (result === 0) {
        return true;
    }
    if (result > 0) {
        return false;
    }
    return true;
};
/**
 * Utility function to check if entered date is less than or equal to reference date with ms accuracy
 * @param date date to compare with reference date
 * @param maxDate reference date
 * @returns boolean value indicating the comparison result
 */
export const isLessThanOrEqual = (date, maxDate) => {
    const dateToTest = getDateInMs({ value: date });
    const referenceDate = getDateInMs({ value: maxDate });
    if (!dateToTest || !referenceDate) {
        return false;
    }
    const result = referenceDate - dateToTest;
    if (result === 0) {
        return true;
    }
    if (result > 0) {
        return true;
    }
    return false;
};
export const defaultDateToDisplay = (minDate, maxDate) => {
    const now = resetTimeOnDate(new Date());
    if (minDate) {
        return getDate({ value: minDate }) ?? now;
    }
    if (maxDate) {
        return getDate({ value: maxDate }) ?? now;
    }
    return now;
};
export const isDateWithinRange = (date, minDate, maxDate, throwOnError = true) => {
    const processedDate = getDate({ value: date, throwOnError: throwOnError });
    const processedMinDate = getDate({ value: minDate, throwOnError: throwOnError });
    const processedMaxDate = getDate({ value: maxDate, throwOnError: throwOnError });
    if (!processedDate || !processedMinDate || !processedMaxDate) {
        return false;
    }
    try {
        return isWithinInterval(processedDate, { start: resetTimeOnDate(processedMinDate), end: resetTimeOnDate(processedMaxDate, true) });
    }
    catch (e) {
        if (throwOnError) {
            throw e;
        }
        return false;
    }
};
/**
 * Utility to check if the current date is before the reference date
 * @param date date to validate
 * @param minDate reference date to validate against
 * @param throwOnError flag to indicate if error needs to be thrown on failure
 * @returns boolean value indicating comparison result.
 */
export const isDateLessThan = (date, maxDate, throwOnError = true) => {
    const processedDate = getDate({ value: date, throwOnError: throwOnError });
    const processedMaxDate = getDate({ value: maxDate, throwOnError: throwOnError });
    if (!processedDate || !processedMaxDate) {
        return false;
    }
    try {
        return compareAsc(processedDate, resetTimeOnDate(processedMaxDate, true)) === 1 ? false : true;
    }
    catch (e) {
        if (throwOnError) {
            throw e;
        }
        return false;
    }
};
/**
 * Utility to check if the current date is after the reference date
 * @param date date to validate
 * @param minDate reference date to validate against
 * @param throwOnError flag to indicate if error needs to be thrown on failure
 * @returns
 */
export const isDateGreaterThan = (date, minDate, throwOnError = true) => {
    const processedDate = getDate({ value: date, throwOnError: throwOnError });
    const processedMinDate = getDate({ value: minDate, throwOnError: throwOnError });
    if (!processedDate || !processedMinDate) {
        return false;
    }
    try {
        return compareAsc(processedDate, resetTimeOnDate(processedMinDate)) === -1 ? false : true;
    }
    catch (e) {
        if (throwOnError) {
            throw e;
        }
        return false;
    }
};
export const resetTimeOnDate = (dateToReset, resetToMaxTime = false) => {
    if (!dateToReset) {
        return new Date(NaN);
    }
    return resetTimeOnDates([dateToReset], resetToMaxTime)[0];
};
/**
 * Utility to reset time on date. Used for date comparison
 * @param datesToReset Date to reset time on
 * @returns date with time set to 00:00:00:000 OR 23:59:59:999 hours UTC
 */
export const resetTimeOnDates = (datesToReset, setEndOfDay = false) => datesToReset.map((d) => (setEndOfDay ? endOfDay(d) : startOfDay(d)));
/**
 * Set time on Date in local timezone.
 */
export const setTimeOnDate = (date, hours, min, sec, ms) => {
    date.setHours(hours, min, sec, ms);
    return date;
};
/**
 *
 * @returns Utility to get current browser local date format. Supports only US and UK date format at the moment.
 */
export const getLocalDateFormat = () => {
    return new Date("2020-12-31").toLocaleDateString().startsWith((31.0).toString()) ? "dd/MM/yyyy" : "MM/dd/yyyy";
};
/**
 * Formats a date for display in a short date format like "2 Jun 2021"
 * @param value the date to format
 * @param timeZone the timezone information
 * @returns the formatted date for display
 */
export function formatShortDateDisplay(value, timeZone) {
    if (!value) {
        return "";
    }
    const date = value instanceof Date ? value : new Date(value);
    return intlFormat(date, {
        year: "numeric",
        month: "short",
        day: "numeric",
        timeZone: timeZone
    }, { locale: navigator.language });
}
;
/**
 * Formats a date for display in a short date and time format like "2 Jun 2021 13:52"
 * @param value the date to format
 * @param timeZone the timezone information
 * @returns the formatted date and time for display
 */
export function formatShortDateTimeDisplay(value, timeZone) {
    if (!value) {
        return "";
    }
    const date = value instanceof Date ? value : new Date(value);
    const newDate = intlFormat(date, {
        minute: "numeric",
        hour: "numeric",
        year: "numeric",
        month: "short",
        day: "numeric",
        timeZone: timeZone,
        hour12: false
    }, { locale: navigator.language });
    return newDate.replace(",", "");
}
;
